import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getStops } from '../../api/endpoints/admin';
import { DataTable } from '../../components/ui/DataTable';
import { SectionCard } from '../../components/ui/SectionCard';
import { formatIst } from './delayReason';
import { shipmentStatusLabel } from './shipmentStatusLabel';
import {
  formatStopMinutes,
  stopEndSourceLabel,
  stopPayerLabel,
  stopReasonLabel,
} from './stopLabels';
import { stopsQueryKey } from './stopQueries';
import styles from './StopHistoryCard.module.css';

/** Stop history of a trip (dnm-work#165 C2 GET …/stops), newest first as the server sends it. */
export function StopHistoryCard({ shipmentId }: { shipmentId: number }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: stopsQueryKey(shipmentId),
    queryFn: () => getStops(shipmentId),
  });

  return (
    <SectionCard title={t('stops.history.title')}>
      <DataTable
        loading={isLoading}
        error={isError ? t('stops.history.loadError') : undefined}
        onRetry={() => refetch()}
        keyFn={(s) => s.id}
        rows={data ?? []}
        empty={t('stops.history.empty')}
        columns={[
          {
            key: 'reason',
            header: t('stops.history.colReason'),
            render: (s) => (
              <>
                {stopReasonLabel(t, s.reason)}
                {s.note && <span className={styles.note}>{s.note}</span>}
                {/* Responsible sits under the reason so the table fits at 1200 px @125%. */}
                <span className={styles.meta}>
                  <span className={styles.metaLabel}>{t('stops.history.colPayer')}</span>{' '}
                  <span className={s.payer ? undefined : styles.notSet}>
                    {stopPayerLabel(t, s.payer)}
                  </span>
                </span>
              </>
            ),
          },
          {
            key: 'stage',
            header: t('stops.history.colStage'),
            render: (s) => <span title={s.stage}>{shipmentStatusLabel(s.stage)}</span>,
          },
          {
            key: 'when',
            header: t('stops.history.colWhen'),
            render: (s) => (
              <>
                <span className={styles.time}>{formatIst(s.startedAt)}</span>{' '}
                <span className={styles.time}>
                  → {s.endedAt ? formatIst(s.endedAt) : t('stops.history.stillOpen')}
                </span>
              </>
            ),
          },
          {
            key: 'duration',
            header: t('stops.history.colDuration'),
            render: (s) => formatStopMinutes(t, s.durationMinutes),
          },
          {
            key: 'ended',
            header: t('stops.history.colEnded'),
            render: (s) => stopEndSourceLabel(t, s.endedAt ? s.endSource : null),
          },
        ]}
      />
    </SectionCard>
  );
}
